/**
 * Prorrateo del flete a partir de cómo entra la carga de verdad.
 *
 * Este es el vínculo entre el armado 3D y el costo, y es la razón por la que el
 * armado no es un adorno.
 *
 * **El problema que resuelve.** El flete de un contenedor es un monto fijo: se
 * paga lo mismo vaya lleno o vaya al 60 %. Para saber cuánto cuesta cada producto
 * hay que repartir ese monto entre los productos, y el criterio del reparto
 * cambia el resultado por producto — a veces mucho.
 *
 * Antes el reparto usaba el volumen **tipeado a mano**. Ahora usa el volumen que
 * las cajas ocupan realmente según el armado, incluido el aire que queda entre
 * ellas: si un producto obliga a dejar espacio muerto, ese espacio es parte de lo
 * que hace falta pagar por traerlo.
 *
 * Todo puro: recibe números y devuelve números, sin tocar three.js ni la red.
 * El reparto de plata se testea con aritmética, no arrastrando cajas.
 */

/** Un producto del embarque, con lo que hace falta para repartirle el flete. */
export interface ProrationItem {
  id: string;
  description: string;
  /** Unidades de este producto en el contenedor. */
  quantity: number;
  /** Volumen de UNA unidad, en m³. */
  unitVolumeCbm: number;
  /** Peso de UNA unidad, en kg. */
  unitWeightKg: number;
  /** FOB de UNA unidad. Necesario para el criterio por valor. */
  unitFobValue: number;
}

/**
 * Criterio de reparto.
 *
 * No hay uno "correcto" para todos los casos, y por eso se elige:
 *
 *  - `volume` — el default. El flete marítimo se cobra por contenedor y lo que
 *    llena un contenedor es el volumen. Es el que corresponde salvo que la carga
 *    sea pesada y voluminosa a la vez.
 *  - `weight` — cuando el límite lo pone la balanza y no el espacio (repuestos,
 *    líquidos, cerámica). Ahí el que consume el contenedor es el peso.
 *  - `value` — reparte proporcional al FOB. Se usa cuando el embarque mezcla
 *    cosas muy distintas y el criterio comercial es que lo caro absorba más
 *    flete. **Ojo:** no representa el consumo real del contenedor.
 *  - `units` — partes iguales por unidad. Solo sirve si todo es parecido.
 */
export type ProrationBasis = 'volume' | 'weight' | 'value' | 'units';

export const BASIS_LABEL: Record<ProrationBasis, string> = {
  volume: 'Por volumen',
  weight: 'Por peso',
  value: 'Por valor FOB',
  units: 'Por unidad',
};

export const BASIS_EXPLANATION: Record<ProrationBasis, string> = {
  volume: 'Lo que llena un contenedor es el volumen. Es el criterio habitual.',
  weight: 'Cuando el límite lo pone la balanza y no el espacio.',
  value: 'Lo más caro absorbe más flete. Es un criterio comercial, no físico.',
  units: 'Partes iguales. Solo sirve si todos los productos son parecidos.',
};

export interface ProratedItem extends ProrationItem {
  /** Cuánto de la base total le corresponde a este producto (0 a 1). */
  share: number;
  /** Flete asignado al producto entero. */
  freightTotal: number;
  /** Flete asignado a UNA unidad. Es el número que va al costo nacionalizado. */
  freightPerUnit: number;
}

export interface ProrationResult {
  items: ProratedItem[];
  basis: ProrationBasis;
  /** Volumen que ocupan las cajas, sumado. */
  netVolumeCbm: number;
  /**
   * Volumen que el armado realmente consume, incluido el aire entre cajas.
   *
   * `null` si no hay un armado hecho: ahí el prorrateo cae al volumen neto y se
   * dice, en vez de simular una precisión que no existe.
   */
  occupiedVolumeCbm: number | null;
  /** Ocupación del contenedor, 0 a 100. `null` sin armado. */
  fillPercent: number | null;
  /** Suma de lo repartido. Tiene que ser igual al flete total. */
  distributedTotal: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

// ── Reparto entre contenedores ───────────────────────────────────────────────

/** Un contenedor del embarque, con lo que hace falta para repartirle el flete. */
export interface ContainerShare {
  id: string;
  /** Cómo lo ve el usuario: "MSCU-4471820" o "Contenedor 1". */
  label: string;
  /**
   * Volumen que consume su armado, del 3D. `null` si todavía no se armó.
   *
   * Es la base del reparto cuando está: un contenedor que va lleno consume más
   * del flete que uno que va a medias.
   */
  occupiedVolumeCbm: number | null;
  /** Volumen interno del equipo. Se usa como respaldo si no hay armado. */
  capacityCbm: number | null;
}

export interface ContainerFreightShare extends ContainerShare {
  /** Parte del flete total que le corresponde a este contenedor. */
  freightAmount: number;
  /** Cuánto del total representa, 0 a 1. */
  share: number;
  /**
   * Cómo se decidió su parte.
   *
   * `occupied` es la buena: sale del armado real. `capacity` reparte por el
   * tamaño del equipo cuando ninguno está armado. `equal` es el último recurso.
   */
  basis: 'occupied' | 'capacity' | 'equal';
}

/**
 * Reparte el flete del embarque **entre los contenedores**, antes de repartirlo
 * entre los productos de cada uno.
 *
 * **Por qué existe.** Un embarque de 520 productos no entra en un solo
 * contenedor: necesita dos o tres. El flete que se cotizó es el de todos juntos.
 * Antes cada contenedor recibía el flete **completo** de la orden, así que el
 * costo por unidad salía multiplicado por la cantidad de contenedores — y el
 * error va hacia arriba, o sea que se cotiza más caro de lo real y se pierde la
 * venta sin saber por qué.
 *
 * **Cómo decide.** Por lo que cada contenedor consume de verdad, con dos
 * respaldos:
 *
 *  1. **Ocupación real del armado** — la correcta. Un contenedor al 90 % se
 *     lleva más flete que uno al 40 %.
 *  2. **Capacidad del equipo** — si ninguno está armado todavía. Un 40HQ paga
 *     más que un 20ST, que es lo que efectivamente cobra la naviera.
 *  3. **Partes iguales** — último recurso, cuando no hay ni armado ni capacidad.
 *
 * El criterio elegido se devuelve en `basis`, para que la pantalla pueda decir
 * de dónde salió el número en vez de mostrarlo sin explicación.
 */
export function prorateAcrossContainers(params: {
  freightTotal: number;
  containers: ContainerShare[];
}): {
  containers: ContainerFreightShare[];
  distributedTotal: number;
  basis: ContainerFreightShare['basis'];
} {
  const { freightTotal, containers } = params;

  if (containers.length === 0) {
    return { containers: [], distributedTotal: 0, basis: 'equal' };
  }

  /*
    Se elige UN criterio para todos, no uno por contenedor.

    Mezclar sería injusto: si el contenedor 1 está armado (53 m³) y el 2 no,
    comparar 53 m³ contra la capacidad de 76 m³ del 2 le daría más flete al que
    ni se armó. Se usa la ocupación solo si TODOS la tienen.
  */
  const allOccupied = containers.every(
    (c) => c.occupiedVolumeCbm !== null && c.occupiedVolumeCbm > 0
  );
  const allCapacity = containers.every((c) => c.capacityCbm !== null && c.capacityCbm > 0);

  const basis: ContainerFreightShare['basis'] = allOccupied
    ? 'occupied'
    : allCapacity
      ? 'capacity'
      : 'equal';

  const measure = (c: ContainerShare): number => {
    if (basis === 'occupied') return c.occupiedVolumeCbm ?? 0;
    if (basis === 'capacity') return c.capacityCbm ?? 0;
    return 1;
  };

  const measures = containers.map(measure);
  const total = measures.reduce((a, b) => a + b, 0);

  // No debería pasar por cómo se eligió `basis`, pero si pasara, partes iguales
  // es una respuesta defendible y NaN no lo es.
  if (total <= 0) {
    const each = round2(freightTotal / containers.length);
    const shares = containers.map((c) => ({
      ...c,
      freightAmount: each,
      share: 1 / containers.length,
      basis: 'equal' as const,
    }));
    return closeTheGap(shares, freightTotal, 'equal');
  }

  const shares: ContainerFreightShare[] = containers.map((c, i) => {
    const share = measures[i] / total;
    return { ...c, share, freightAmount: round2(freightTotal * share), basis };
  });

  return closeTheGap(shares, freightTotal, basis);
}

/**
 * Ajusta el centavo que deja el redondeo, para que la suma sea exactamente el
 * flete cotizado.
 *
 * Repartir 20.880 entre tres contenedores da 6.960 exacto, pero 10.440 entre
 * tres da 3.480 × 3 = 10.440 y entre siete no cierra. La diferencia se le suma
 * al de mayor participación, que es donde menos se nota. Un centavo que no cuadra
 * es un centavo que la contabilidad va a buscar.
 */
function closeTheGap(
  shares: ContainerFreightShare[],
  freightTotal: number,
  basis: ContainerFreightShare['basis']
): {
  containers: ContainerFreightShare[];
  distributedTotal: number;
  basis: ContainerFreightShare['basis'];
} {
  const distributed = shares.reduce((sum, c) => sum + c.freightAmount, 0);
  const drift = round2(freightTotal - distributed);

  if (Math.abs(drift) >= 0.01 && shares.length > 0) {
    let biggest = 0;
    for (let i = 1; i < shares.length; i++) {
      if (shares[i].share > shares[biggest].share) biggest = i;
    }
    shares[biggest].freightAmount = round2(shares[biggest].freightAmount + drift);
  }

  return {
    containers: shares,
    distributedTotal: round2(shares.reduce((sum, c) => sum + c.freightAmount, 0)),
    basis,
  };
}

/**
 * Reparte el flete entre los productos.
 *
 * Devuelve los ítems con su flete por unidad y la traza de cómo se llegó ahí.
 *
 * Casos que se resuelven a propósito y no por accidente:
 *
 *  - **Base total cero** (todos los volúmenes en cero porque nadie cargó
 *    dimensiones): se cae a partes iguales por unidad. Repartir dividiendo por
 *    cero daría `NaN` y ese `NaN` terminaría en un costo nacionalizado.
 *  - **Redondeo**: repartir 10.440 entre tres productos da centavos que no
 *    cierran. La diferencia se le suma al ítem de mayor participación, así la
 *    suma de lo repartido es exactamente el flete. Un centavo perdido en un
 *    contenedor es un centavo que no cuadra en la contabilidad.
 */
export function prorateFreight(params: {
  freightTotal: number;
  items: ProrationItem[];
  basis?: ProrationBasis;
  /** Volumen que consume el armado, del 3D. Sin esto se usa el volumen neto. */
  occupiedVolumeCbm?: number | null;
  /** Volumen máximo del contenedor, para el porcentaje de ocupación. */
  containerVolumeCbm?: number | null;
}): ProrationResult {
  const {
    freightTotal,
    items,
    basis = 'volume',
    occupiedVolumeCbm = null,
    containerVolumeCbm = null,
  } = params;

  const netVolumeCbm = round2(items.reduce((sum, i) => sum + i.unitVolumeCbm * i.quantity, 0));

  const fillPercent =
    occupiedVolumeCbm !== null && containerVolumeCbm !== null && containerVolumeCbm > 0
      ? Math.round((occupiedVolumeCbm / containerVolumeCbm) * 1000) / 10
      : null;

  if (items.length === 0) {
    return {
      items: [],
      basis,
      netVolumeCbm,
      occupiedVolumeCbm,
      fillPercent,
      distributedTotal: 0,
    };
  }

  /** Cuánto "consume" cada ítem según el criterio elegido. */
  const measure = (item: ProrationItem): number => {
    switch (basis) {
      case 'weight':
        return item.unitWeightKg * item.quantity;
      case 'value':
        return item.unitFobValue * item.quantity;
      case 'units':
        return item.quantity;
      case 'volume':
      default:
        return item.unitVolumeCbm * item.quantity;
    }
  };

  let measures = items.map(measure);
  let total = measures.reduce((a, b) => a + b, 0);

  // Sin base no se puede repartir proporcionalmente. Partes iguales por unidad
  // es una respuesta defendible; NaN no.
  if (total <= 0) {
    measures = items.map((i) => i.quantity);
    total = measures.reduce((a, b) => a + b, 0);
  }
  // Si ni siquiera hay cantidades, todo va a cero: no hay nada que repartir.
  if (total <= 0) {
    return {
      items: items.map((item) => ({ ...item, share: 0, freightTotal: 0, freightPerUnit: 0 })),
      basis,
      netVolumeCbm,
      occupiedVolumeCbm,
      fillPercent,
      distributedTotal: 0,
    };
  }

  const prorated: ProratedItem[] = items.map((item, index) => {
    const share = measures[index] / total;
    const assigned = round2(freightTotal * share);
    return {
      ...item,
      share,
      freightTotal: assigned,
      freightPerUnit: item.quantity > 0 ? round2(assigned / item.quantity) : 0,
    };
  });

  // El redondeo hace que la suma no cierre. La diferencia va al de mayor
  // participación, que es donde menos se nota y donde más corresponde.
  const distributed = prorated.reduce((sum, i) => sum + i.freightTotal, 0);
  const drift = round2(freightTotal - distributed);
  if (Math.abs(drift) >= 0.01) {
    let biggest = 0;
    for (let i = 1; i < prorated.length; i++) {
      if (prorated[i].share > prorated[biggest].share) biggest = i;
    }
    prorated[biggest].freightTotal = round2(prorated[biggest].freightTotal + drift);
    prorated[biggest].freightPerUnit =
      prorated[biggest].quantity > 0
        ? round2(prorated[biggest].freightTotal / prorated[biggest].quantity)
        : 0;
  }

  return {
    items: prorated,
    basis,
    netVolumeCbm,
    occupiedVolumeCbm,
    fillPercent,
    distributedTotal: round2(prorated.reduce((sum, i) => sum + i.freightTotal, 0)),
  };
}

/**
 * Cuánto volumen consume realmente el armado.
 *
 * No es la suma de las cajas: es el prisma que las contiene, o sea las cajas más
 * el aire que queda entre ellas. Ese aire también viaja, y también se paga.
 *
 * Se calcula desde el punto más lejano alcanzado en cada eje. Es una
 * aproximación conservadora — un contenedor con carga en las cuatro esquinas y
 * nada en el medio da el prisma completo — pero es la que corresponde: ese
 * contenedor efectivamente no puede llevar otra cosa.
 */
export function occupiedVolumeFromPlacements(
  placements: Array<{
    x: number;
    y: number;
    z: number;
    lengthCm: number;
    widthCm: number;
    heightCm: number;
  }>
): number {
  if (placements.length === 0) return 0;

  let maxX = 0;
  let maxY = 0;
  let maxZ = 0;
  for (const p of placements) {
    maxX = Math.max(maxX, p.x + p.lengthCm);
    maxY = Math.max(maxY, p.y + p.widthCm);
    maxZ = Math.max(maxZ, p.z + p.heightCm);
  }

  // cm³ → m³
  return round2((maxX * maxY * maxZ) / 1_000_000);
}

/**
 * Cuánto cambió el flete por unidad respecto de lo que se había estimado.
 *
 * Es el número que hace que el armado valga la pena: dice si la cotización que
 * se le mandó al cliente se sostiene o si el costo real se movió.
 */
export function freightDelta(
  estimatedPerUnit: number,
  actualPerUnit: number
): { absolute: number; percent: number | null } {
  const absolute = round2(actualPerUnit - estimatedPerUnit);
  return {
    absolute,
    percent: estimatedPerUnit > 0 ? Math.round((absolute / estimatedPerUnit) * 1000) / 10 : null,
  };
}

// ── Lo que no entró ──────────────────────────────────────────────────────────

/** Un producto que el armado no pudo ubicar, con lo que ocupa. */
export interface UnplacedSummaryItem {
  id: string;
  description: string;
  quantity: number;
  volumeCbm: number;
  weightKg: number;
}

export interface UnplacedSummary {
  items: UnplacedSummaryItem[];
  totalVolumeCbm: number;
  totalWeightKg: number;
  totalUnits: number;
  /**
   * Cuántos contenedores más harían falta para lo que sobró.
   *
   * Se calcula por volumen Y por peso, y gana el mayor: un contenedor puede
   * llenarse por espacio o por balanza, y el que se llene primero es el que manda.
   * Estimar solo por volumen daría "un contenedor más" para 30 toneladas de
   * cerámica que ocupan poco pero pesan el doble del límite.
   */
  extraContainersNeeded: number;
}

const round2u = (n: number) => Math.round(n * 100) / 100;

/**
 * Resume lo que el armado dejó afuera.
 *
 * `packContainer` devuelve los ids de lo que no pudo ubicar, y hasta ahora la
 * pantalla solo decía "3 items no pudieron ser colocados" — sin cuánto ocupan ni
 * qué hacer con ellos. El operador quedaba con un aviso y ninguna acción.
 *
 * Puro: recibe los ids y los productos, y devuelve la aritmética.
 */
export function summarizeUnplaced(params: {
  unplacedIds: string[];
  items: Array<{
    id: string;
    description: string;
    quantity: number;
    unitVolumeCbm: number;
    unitWeightKg: number;
  }>;
  /** Capacidad del equipo con el que se armaría el siguiente contenedor. */
  containerVolumeCbm: number | null;
  containerMaxCargoKg: number | null;
}): UnplacedSummary {
  const { unplacedIds, items, containerVolumeCbm, containerMaxCargoKg } = params;

  const ids = new Set(unplacedIds);
  const unplaced = items.filter((i) => ids.has(i.id));

  const summary: UnplacedSummaryItem[] = unplaced.map((i) => ({
    id: i.id,
    description: i.description,
    quantity: i.quantity,
    volumeCbm: round2u(i.unitVolumeCbm * i.quantity),
    weightKg: round2u(i.unitWeightKg * i.quantity),
  }));

  const totalVolumeCbm = round2u(summary.reduce((s, i) => s + i.volumeCbm, 0));
  const totalWeightKg = round2u(summary.reduce((s, i) => s + i.weightKg, 0));
  const totalUnits = summary.reduce((s, i) => s + i.quantity, 0);

  // Por volumen y por peso; gana el mayor.
  const byVolume =
    containerVolumeCbm && containerVolumeCbm > 0
      ? Math.ceil(totalVolumeCbm / containerVolumeCbm)
      : 0;
  const byWeight =
    containerMaxCargoKg && containerMaxCargoKg > 0
      ? Math.ceil(totalWeightKg / containerMaxCargoKg)
      : 0;

  return {
    items: summary,
    totalVolumeCbm,
    totalWeightKg,
    totalUnits,
    // Al menos uno si algo sobró: sin capacidad conocida no se puede estimar, pero
    // decir "0 contenedores más" cuando sobró carga sería peor que decir "1".
    extraContainersNeeded: summary.length === 0 ? 0 : Math.max(1, byVolume, byWeight),
  };
}
